/**
 * LoggingService - Core logging service for Mentorist React Native
 * 
 * Handles multi-channel logging to Firebase Analytics and custom Firebase Functions
 * Following the old iOS app pattern with improvements
 * 
 * Features:
 * - Platform-aware logging (web uses [rn] prefix, native uses no prefix)
 * - itemId-based grouping for analytics
 * - Sequential async operations via PromisesQueue
 * - Retry logic with exponential backoff
 * - Firebase Analytics integration
 * - Custom saveLog function integration (Mixpanel, etc.)
 * - Device info saved to user profile once (not sent with every log - matches iOS app pattern)
 * 
 * IMPORTANT: Device info is now stored in the user profile on app start, not sent with every log.
 * This makes logs simpler and reduces data transfer, matching the old iOS app architecture.
 */

import { Platform, Linking, Dimensions } from 'react-native';
import { auth, functions, logAnalyticsEvent, onAuthStateChanged, httpsCallable } from '../config/firebase';
import { PromisesQueue } from '../utils/logging/PromisesQueue';
import { Monitor } from '../utils/logging/Monitor';
import { LogAction, LogActionType } from '../utils/logging/actions';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Web Firebase imports (lazy loaded)
let webSaveLog: any;

// Initialize web Firebase lazily when needed
const initWebFirebase = () => {
  if (Platform.OS === 'web' && !webSaveLog) {
    try {
      if (!functions) {
        if (__DEV__) {
          console.warn('Functions not initialized yet, skipping webSaveLog setup');
        }
        return;
      }
      webSaveLog = httpsCallable(functions, 'saveLog');
    } catch (error) {
      console.error('Failed to initialize web Firebase:', error);
    }
  }
};

export interface LogParams {
  [key: string]: any;
}

export class LoggingService {
  private static instance: LoggingService;
  private static queue = new PromisesQueue();
  private static monitor = new Monitor();
  private static isInitialized = false;
  private static userId: string | null = null;
  private static platformPrefix: string = '';
  private static appVersion: string = '';
  private static colorScheme: 'light' | 'dark' = 'light';

  private constructor() {
    if (!LoggingService.isInitialized) {
      this.initialize();
    }
  }

  static getInstance(): LoggingService {
    if (!LoggingService.instance) {
      LoggingService.instance = new LoggingService();
    }
    return LoggingService.instance;
  }

  private async initialize(): Promise<void> {
    LoggingService.queue.add(async () => {
      try {
        // Wait for auth to be ready
        await this.waitForAuth();
        
        // Determine platform prefix
        LoggingService.platformPrefix = this.getPlatformPrefix();
        
        // Get app version
        try {
          // Try to get from Constants (Expo)
          const Constants = require('expo-constants').default;
          LoggingService.appVersion = Constants.expoConfig?.version || Constants.manifest?.version || '1.0.0';
        } catch {
          LoggingService.appVersion = '6.0.0';
        }
        
        // Save device info to user profile once (not sent with every log)
        // This is done asynchronously and doesn't block logging initialization
        // Note: May fail due to Firestore permissions - that's OK, device info is nice-to-have
        if (LoggingService.userId) {
          this.saveDeviceInfoToProfile().catch(error => {
            if (__DEV__) {
              console.warn('⚠️ Failed to save device info to profile (non-critical):', error.message);
            }
            // Don't block logging even if device info save fails - logs will still work fine
          });
        }
        
        // Log app opened event
        await this.logAppOpened();
        
        LoggingService.monitor.done();
        LoggingService.isInitialized = true;
        
        if (__DEV__) {
          console.log('✅ LoggingService initialized', {
            platform: LoggingService.platformPrefix,
            userId: LoggingService.userId,
            appVersion: LoggingService.appVersion,
          });
        }
      } catch (error) {
        console.error('❌ Failed to initialize LoggingService:', error);
        LoggingService.monitor.done(); // Don't block even on error
      }
    });
  }

  private getPlatformPrefix(): string {
    // Only use 'rn' prefix on web - native apps use no prefix
    return Platform.OS === 'web' ? 'rn' : '';
  }

  private getDeviceLanguageSafe(): string {
    try {
      // On web, use navigator.language
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && (navigator as any).language) {
        return (navigator as any).language;
      }
      
      // On native, try to get device language from react-native-localize or system settings
      // For now, fall back to 'en' - can be enhanced later with expo-localization if needed
    } catch {}
    return 'en';
  }

  private async collectAppOpenParams(): Promise<Record<string, any>> {
    const params: Record<string, any> = {};

    if (Platform.OS === 'web') {
      try {
        // Entry URL and referrer (web-specific, important for attribution)
        const href = (window as any)?.location?.href;
        const pathname = (window as any)?.location?.pathname;
        const referrer = (document as any)?.referrer || '';
        params.entryUrl = href || '';
        params.entryPath = pathname || '';
        if (referrer) params.referrer = referrer;

        // UTM & campaign params (important for attribution)
        const search = (window as any)?.location?.search || '';
        const usp = new URLSearchParams(search);
        const keys = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','gclid','fbclid'];
        keys.forEach((k) => {
          const v = usp.get(k);
          if (v) params[k] = v;
        });
      } catch {}
    } else {
      // Native deep link (important for attribution)
      try {
        const initialUrl = await Linking.getInitialURL();
        if (initialUrl) {
          params.initialDeepLink = initialUrl;
          try {
            const url = new URL(initialUrl);
            const keys = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','gclid','fbclid'];
            keys.forEach((k) => {
              const v = url.searchParams.get(k);
              if (v) params[k] = v;
            });
          } catch {}
        }
      } catch {}
    }

    return params;
  }

  /**
   * Save device info to user profile once (not sent with every log)
   * This matches the old iOS app pattern where device info is stored in profile
   */
  private async saveDeviceInfoToProfile(): Promise<void> {
    try {
      if (!LoggingService.userId) {
        console.warn('⚠️ Cannot save device info: no userId');
        return;
      }

      // Import PrivateProfileService dynamically to avoid circular dependencies
      const { PrivateProfileService } = await import('./privateProfileService');
      
      // Save device info to profile
      await PrivateProfileService.updateUserDeviceInfo(
        LoggingService.userId,
        LoggingService.appVersion
      );
      
      if (__DEV__) {
        console.log('✅ Device info saved to user profile');
      }
    } catch (error) {
      console.error('Failed to save device info to profile:', error);
      // Don't throw - this is non-critical
    }
  }

  private async waitForAuth(): Promise<void> {
    return new Promise((resolve) => {
      if (!onAuthStateChanged || !auth) {
        resolve();
        return;
      }

      try {
        const unsubscribe = onAuthStateChanged(auth, (user: any) => {
          if (user) {
            LoggingService.userId = user.uid;
          }
          unsubscribe?.();
          resolve();
        });
      } catch (error) {
        console.error('Failed to setup auth listener:', error);
        resolve();
      }
    });
  }


  private async logAppOpened(): Promise<void> {
    const eventName = `[${LoggingService.platformPrefix}] App Opened`;
    const params = await this.collectAppOpenParams();
    await this.sendToFirebase(eventName, null, null, params);
  }

  /**
   * Main logging method - call this to log any action
   */
  async log(action: LogAction): Promise<void> {
    // Wait for initialization
    await LoggingService.monitor.waitForInit();
    
    // Queue the logging operation
    LoggingService.queue.add(async () => {
      try {
        await this.processAction(action);
      } catch (error) {
        console.error('[LoggingService] Failed to process action:', action.type, error);
      }
    });
  }

  private async processAction(action: LogAction): Promise<void> {
    const eventName = this.getEventName(action);
    const params = action.params || {};
    const value = action.itemId || null;  // itemId becomes value
    const onboardingName = params.onboardingName || null;
    
    // Log to all channels in parallel
    await Promise.all([
      this.sendToFirebaseAnalytics(action),
      this.sendToFirebase(eventName, value, onboardingName, params),
    ]);
  }

  private getEventName(action: LogAction): string {
    // Use static event names - no dynamic elements!
    // Dynamic values go to itemId or params
    const prefix = LoggingService.platformPrefix;
    const prefixStr = prefix ? `[${prefix}] ` : '';
    
    switch (action.type) {
      case LogActionType.SCREEN_VIEWED:
        // Screen name is carried in params.screenName
        return `${prefixStr}Viewed: ${action.params?.screenName ?? 'Unknown'}`;
      case LogActionType.BOOK_OPENED:
        return `${prefixStr}Book Opened`;
      case LogActionType.CHAPTER_OPENED:
        return `${prefixStr}Chapter Opened`;
      case LogActionType.INSIGHT_OPENED:
        return `${prefixStr}Insight Opened`;
      case LogActionType.INSIGHT_COMPLETED:
        return `${prefixStr}Insight Completed`;
      case LogActionType.BOOK_ADDED_TO_FAVORITES:
        return `${prefixStr}Book Added to Favorites`;
      case LogActionType.BOOK_REMOVED_FROM_FAVORITES:
        return `${prefixStr}Book Removed from Favorites`;
      case LogActionType.CHAT_MESSAGE_SENT:
        return `${prefixStr}Chat Message Sent`;
      case LogActionType.CHAT_MESSAGE_RECEIVED:
        return `${prefixStr}Chat Message Received`;
      case LogActionType.QUICK_ACTION_PRESSED:
        return `${prefixStr}Quick Action Pressed`;
      case LogActionType.BUTTON_PRESSED:
        return `${prefixStr}Button Pressed`;
      case LogActionType.FORM_SUBMITTED:
        return `${prefixStr}Form Submitted`;
      case LogActionType.ONBOARDING_STEP_COMPLETED:
        // Use human-readable slug from params
        const questionSlug = action.params?.questionSlug || 'unknown';
        return `${prefixStr}Onboarding: ${questionSlug}`;
      case LogActionType.ONBOARDING_STARTED:
        return `${prefixStr}Onboarding Started`;
      case LogActionType.ONBOARDING_COMPLETED:
        return `${prefixStr}Onboarding Completed`;
      case LogActionType.USER_SIGNED_UP:
        return `${prefixStr}User Signed Up`;
      case LogActionType.USER_SIGNED_IN:
        return `${prefixStr}User Signed In`;
      case LogActionType.USER_SIGNED_OUT:
        return `${prefixStr}User Signed Out`;
      case LogActionType.APP_BACKGROUNDED:
        return `${prefixStr}App Backgrounded`;
      case LogActionType.APP_FOREGROUND:
        return `${prefixStr}App Foregrounded`;
      case LogActionType.ERROR_OCCURRED:
        return `${prefixStr}Error Occurred`;
      case LogActionType.EXTERNAL_TRANSFER_STARTED:
        return `${prefixStr}External Transfer Started`;
      case LogActionType.EXTERNAL_TRANSFER_COMPLETED:
        return `${prefixStr}External Transfer Completed`;
      case LogActionType.USER_CONTEXT:
        return `${prefixStr}User Context`;
      case LogActionType.PAYWALL_VIEWED:
        return `${prefixStr}Paywall Viewed`;
      case LogActionType.PAYWALL_STEP_CHANGED:
        return `${prefixStr}Paywall Step Changed`;
      case LogActionType.PAYWALL_DISMISSED:
        return `${prefixStr}Paywall Dismissed`;
      case LogActionType.PACKAGE_SELECTED:
        return `${prefixStr}Package Selected`;
      case LogActionType.PURCHASE_STARTED:
        return `${prefixStr}Purchase Started`;
      case LogActionType.PURCHASE_COMPLETED:
        return `${prefixStr}Purchase Completed`;
      case LogActionType.PURCHASE_FAILED:
        return `${prefixStr}Purchase Failed`;
      case LogActionType.PURCHASE_CANCELLED:
        return `${prefixStr}Purchase Cancelled`;
      case LogActionType.RESTORE_PURCHASES_STARTED:
        return `${prefixStr}Restore Purchases Started`;
      case LogActionType.PURCHASES_RESTORED:
        return `${prefixStr}Purchases Restored`;
      case LogActionType.RESTORE_PURCHASES_FAILED:
        return `${prefixStr}Restore Purchases Failed`;
      default:
        return `${prefixStr}${action.type}`;
    }
  }

  private async sendToFirebaseAnalytics(action: LogAction): Promise<void> {
    try {
      if (Platform.OS === 'web') {
        initWebFirebase(); // Ensure web Firebase is initialized
      }

      const eventName = this.mapToAnalyticsEvent(action);
      const params = this.buildAnalyticsParams(action);

      await logAnalyticsEvent(eventName, params);
    } catch (error: any) {
      // Analytics is non-critical, silently skip on errors
      if (__DEV__) {
        console.warn('[LoggingService] Firebase Analytics skipped:', error?.message || error);
      }
    }
  }

  private mapToAnalyticsEvent(action: LogAction): string {
    // Map our action types to standard Firebase Analytics events
    switch (action.type) {
      case LogActionType.SCREEN_VIEWED:
        return 'screen_view';
      case LogActionType.BOOK_OPENED:
        return 'select_content';
      case LogActionType.CHAPTER_OPENED:
        return 'select_content';
      case LogActionType.INSIGHT_OPENED:
        return 'select_content';
      case LogActionType.USER_SIGNED_UP:
        return 'sign_up';
      case LogActionType.USER_SIGNED_IN:
        return 'login';
      default:
        // Custom event - sanitize name (lowercase, underscores only)
        return action.type.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    }
  }

  private buildAnalyticsParams(action: LogAction): Record<string, any> {
    // Firebase Analytics has parameter name restrictions
    // Convert our params to Analytics-friendly format
    const params: Record<string, any> = {};
    
    // Add value (itemId) if present
    if (action.itemId) {
      params.value = action.itemId;
      params.item_id = action.itemId; // Also keep item_id for compatibility
    }
    
    if (action.params) {
      Object.entries(action.params).forEach(([key, value]) => {
        // Convert camelCase to snake_case
        const analyticsKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        
        // Firebase Analytics only accepts string, number, or array values
        if (typeof value === 'string' || typeof value === 'number' || Array.isArray(value)) {
          params[analyticsKey] = value;
        } else if (typeof value === 'boolean') {
          params[analyticsKey] = value ? 'true' : 'false';
        } else if (value !== null && value !== undefined) {
          params[analyticsKey] = String(value);
        }
      });
    }
    
    return params;
  }

  /**
   * Public helper: log a safe user context snapshot (e.g., onboarding transfer info)
   */
  async logUserContext(context: Record<string, any>): Promise<void> {
    const { userContext } = require('../utils/logging/actions');
    await this.log(userContext(context));
  }

  private async sendToFirebase(
    eventName: string, 
    value: string | null,
    onboardingName: string | null,
    params: LogParams
  ): Promise<void> {
    const maxRetries = 3;
    let lastError: any;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Enrich params with ONLY critical information (not device info - that's in profile)
        // This matches the old iOS app pattern where logs are simpler
        const enrichedParams = {
          ...params,
          // Critical log-specific metadata only
          appVersion: LoggingService.appVersion,
          // NOTE: userId is at root level, not duplicated in params
          // NOTE: sessionId not needed - not used for analytics
          // NOTE: isTester, countryCode, device info all stored in user profile, not sent with every log
        };
        
        // Build request body exactly like web app (and old iOS app)
        const requestBody: any = {
          name: eventName,
          value,  // Main field for grouping/filtering (was itemId)
          userId: LoggingService.userId,  // At root level - not duplicated in params
          params: enrichedParams,
          superProperties: {},
          returnLogId: true, // Request log ID from backend for debugging
        };
        
        // Add onboardingName at root level if present (like web app)
        if (onboardingName) {
          requestBody.onboardingName = onboardingName;
        }
        
        let response: any;
        if (Platform.OS === 'web') {
          initWebFirebase(); // Ensure web Firebase is initialized

          if (!webSaveLog) {
            // Function not loaded yet, skip
            if (__DEV__) {
              console.warn('webSaveLog not ready, skipping log:', eventName);
            }
            return;
          }
          response = await webSaveLog(requestBody);
        } else {
          if (!functions) {
            if (__DEV__) {
              console.warn('Functions not initialized, skipping log:', eventName);
            }
            return;
          }
          const saveLog = httpsCallable(functions, 'saveLog');
          response = await saveLog(requestBody);
        }
        
        if (__DEV__) {
          const logId = response?.data?.logId || response?.logId;
          console.log('✅ Logged:', eventName, value ? `(${value})` : '', logId ? `[${logId}]` : '');
        }
        
        return; // Success
      } catch (error) {
        lastError = error;
        console.error(`❌ Logging error (attempt ${attempt + 1}/${maxRetries + 1}):`, error);
        
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt) * 1000; // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    console.error('🚨 Logging failed permanently:', lastError);
  }

  /**
   * Convenience methods for common logging scenarios
   */
  async logScreenView(screenName: string, params?: Record<string, any>): Promise<void> {
    const { screenViewed } = require('../utils/logging/actions');
    // Extract a concise value ID from provided params if any
    const valueId = params?.itemId || params?.bookId || params?.insightId || params?.chapterId || null;
    await this.log(screenViewed(screenName, valueId));
  }

  async logBookOpen(bookId: string, bookTitle?: string, source?: string): Promise<void> {
    const { bookOpened } = require('../utils/logging/actions');
    await this.log(bookOpened(bookId, bookTitle, source));
  }

  async logChapterOpen(bookId: string, chapterId: string, chapterTitle?: string): Promise<void> {
    const { chapterOpened } = require('../utils/logging/actions');
    await this.log(chapterOpened(bookId, chapterId, chapterTitle));
  }

  async logButtonPress(buttonId: string, context?: Record<string, any>): Promise<void> {
    const { buttonPressed } = require('../utils/logging/actions');
    await this.log(buttonPressed(buttonId, context));
  }

  /**
   * Insights Feed Events
   */
  async logInsightsFeedView(insightId: string, index: number): Promise<void> {
    const { LogActionType } = require('../utils/logging/actions');
    await this.log({
      type: 'insights_feed_view' as LogActionType,
      timestamp: Date.now(),
      itemId: insightId,
      params: {
        index,
        source: 'insights_feed',
      },
    });
  }

  async logInsightsFeedNextPage(pageNumber: number): Promise<void> {
    const { LogActionType } = require('../utils/logging/actions');
    await this.log({
      type: 'insights_feed_next_page' as LogActionType,
      timestamp: Date.now(),
      itemId: null,
      params: {
        pageNumber,
      },
    });
  }

  async logInsightOpenFromFeed(insightId: string, index: number, action: 'read' | 'listen'): Promise<void> {
    const { LogActionType } = require('../utils/logging/actions');
    await this.log({
      type: 'insight_open_from_feed' as LogActionType,
      timestamp: Date.now(),
      itemId: insightId,
      params: {
        index,
        action,
        source: 'insights_feed',
      },
    });
  }

  async logChatOpenFromHome(): Promise<void> {
    const { LogActionType } = require('../utils/logging/actions');
    await this.log({
      type: 'chat_open_from_home_button' as LogActionType,
      timestamp: Date.now(),
      itemId: null,
      params: {
        source: 'home_mentor_card',
      },
    });
  }
}

export const loggingService = LoggingService.getInstance();

